import { Box, Typography } from '@mui/material';

import {
  ActionIconButton,
  CustomAutoComplete,
  CustomTextField,
} from '../../../../common/components';
import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_TRACEABILITY_OPTIONS,
} from '../../constants/supplierLedgerConstants';
import { VAT_RATE_OPTIONS } from '../../constants/supplierLedgerUiConstants';
import { calculateAmountLine } from '../../utils/supplierLedgerHelpers';
import { formatCurrencyAmount } from '../../utils/supplierLedgerFormatters';
import { FieldSelect } from './SupplierLedgerFormFields';

export const AmountLineCalculatedSummary = ({ net, vat }) => (
  <Box
    sx={{
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
      gap: 1,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      px: 1,
      py: 0.75,
      backgroundColor: 'background.formTables',
      minWidth: 0,
    }}
  >
    {[
      { label: 'Neto calculado', value: net },
      { label: 'IVA calculado', value: vat },
    ].map((item) => (
      <Box
        key={item.label}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          minWidth: 0,
          minHeight: 30,
          px: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}>
          {item.label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.primary', fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}
          title={String(item.value || '-')}
        >
          {item.value || '-'}
        </Typography>
      </Box>
    ))}
  </Box>
);

const conceptHelperText = (options, loading, selectedOption) => {
  if (loading) return 'Cargando conceptos...';
  if (!options.length) return 'No hay conceptos activos cargados.';
  if (selectedOption?.legacy) return 'Este concepto ya no está activo. Seleccioná uno del catálogo.';
  return undefined;
};

const renderConceptOption = (props, option) => {
  const { key, ...optionProps } = props;
  return (
    <Box
      component="li"
      key={key}
      {...optionProps}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.25,
        py: 0.75,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
        {option.label}{option.code ? ` · ${option.code}` : ''}
      </Typography>
      {option.description && (
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
          {option.description}
        </Typography>
      )}
    </Box>
  );
};

const lockedSelectSx = (locked) => ({
  pointerEvents: locked ? 'none' : 'auto',
  opacity: locked ? 0.65 : 1,
});

// En md+ la fila ocupa tres lineas completas: cuenta y concepto; detalle, total e IVA; clasificacion y borrar.
export const AmountLineRow = ({
  accountingAccountOptions = [],
  accountingAccountsLoading = false,
  movementConceptOptions = [],
  movementConceptsLoading = false,
  line,
  index,
  currency,
  canRemove,
  // Factura con OP o pagos: no se pueden tocar importes, pero si el detalle contable de cada linea.
  amountsLocked = false,
  onChange,
  onRemove,
}) => {
  const lineAmounts = calculateAmountLine(line);
  const selectedConcept = movementConceptOptions.find((option) => option.value === line.uuid_movement_concept);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: '36px repeat(12, minmax(0, 1fr)) 56px',
        },
        gap: 1.25,
        alignItems: 'start',
        p: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'flex-start', md: 'center' },
          minHeight: 56,
          mt: '12px',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          color: 'primary.main',
          fontWeight: 800,
          fontSize: '0.9rem',
          backgroundColor: 'background.formTables',
          px: { xs: 1.5, md: 0 },
          gridColumn: { xs: 'span 1', sm: 'span 2', md: '1 / 2' },
          gridRow: { md: '1 / 4' },
          alignSelf: { md: 'stretch' },
        }}
      >
        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
          {`Importe ${index + 1}`}
        </Box>
        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
          {index + 1}
        </Box>
      </Box>
      <CustomAutoComplete
        label="Cuenta contable"
        name={`account-${line.id}`}
        value={line.uuid_accounting_account || ''}
        onChange={(event) => onChange(line.id, 'uuid_accounting_account', event.target.value)}
        options={accountingAccountOptions}
        loading={accountingAccountsLoading}
        disabled={accountingAccountsLoading}
        placeholder="Seleccionar cuenta"
        sx={{ gridColumn: { xs: '1', sm: 'span 2', md: '2 / 9' }, gridRow: { md: '1' } }}
      />
      <CustomAutoComplete
        label="Concepto"
        name={`concept-${line.id}`}
        value={line.uuid_movement_concept || ''}
        onChange={(event) => onChange(line.id, 'uuid_movement_concept', event.target.value)}
        options={movementConceptOptions}
        loading={movementConceptsLoading}
        disabled={movementConceptsLoading || movementConceptOptions.length === 0}
        placeholder="Seleccionar concepto"
        helperText={conceptHelperText(movementConceptOptions, movementConceptsLoading, selectedConcept)}
        getOptionLabel={(option) => option?.label || ''}
        renderOption={renderConceptOption}
        sx={{ gridColumn: { xs: '1', sm: 'span 2', md: '9 / 15' }, gridRow: { md: '1' } }}
      />
      <CustomTextField
        label="Detalle"
        name={`description-${line.id}`}
        value={line.description || ''}
        onChange={(event) => onChange(line.id, 'description', event.target.value)}
        sx={{ gridColumn: { xs: '1', sm: 'span 2', md: '2 / 8' }, gridRow: { md: '2' } }}
      />
      <CustomTextField
        label="Total con IVA"
        name={`amount-${line.id}`}
        value={line.gross_amount}
        onChange={(event) => onChange(line.id, 'gross_amount', event.target.value)}
        placeholder="0,00"
        disabled={amountsLocked}
        sx={{ gridColumn: { xs: '1', sm: 'span 1', md: '8 / 12' }, gridRow: { md: '2' } }}
      />
      <FieldSelect
        label="IVA %"
        name={`vat-rate-${line.id}`}
        value={line.vat_rate}
        onChange={(event) => onChange(line.id, 'vat_rate', event.target.value)}
        options={VAT_RATE_OPTIONS}
        sx={{ width: '100%', gridColumn: { xs: '1', sm: 'span 1', md: '12 / 15' }, gridRow: { md: '2' }, ...lockedSelectSx(amountsLocked) }}
      />
      <FieldSelect
        label="Fijo/Variable"
        name={`cost-behavior-${line.id}`}
        value={line.cost_behavior || 'fixed'}
        onChange={(event) => onChange(line.id, 'cost_behavior', event.target.value)}
        options={COST_BEHAVIOR_OPTIONS}
        sx={{ width: '100%', gridColumn: { xs: '1', sm: 'span 1', md: '2 / 6' }, gridRow: { md: '3' } }}
      />
      <FieldSelect
        label="Ord./Extraord."
        name={`cost-nature-${line.id}`}
        value={line.cost_nature || line.cost_traceability || 'ordinary'}
        onChange={(event) => onChange(line.id, 'cost_nature', event.target.value)}
        options={COST_TRACEABILITY_OPTIONS}
        sx={{ width: '100%', gridColumn: { xs: '1', sm: 'span 1', md: '6 / 10' }, gridRow: { md: '3' } }}
      />
      <FieldSelect
        label="Área"
        name={`cost-area-${line.id}`}
        value={line.cost_area || 'work'}
        onChange={(event) => onChange(line.id, 'cost_area', event.target.value)}
        options={COST_AREA_OPTIONS}
        sx={{ width: '100%', gridColumn: { xs: '1', sm: 'span 2', md: '10 / 14' }, gridRow: { md: '3' } }}
      />
      <Box
        sx={{
          mt: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'flex-start', md: 'center' },
          minHeight: 56,
          gridColumn: { xs: 'span 1', sm: 'span 2', md: '14 / 15' },
          gridRow: { md: '3' },
        }}
      >
        <ActionIconButton
          variant="delete"
          aria-label={`Quitar importe ${index + 1}`}
          onClick={() => onRemove(line.id)}
          disabled={amountsLocked || (!canRemove && !line.gross_amount)}
          sx={{ height: 56, width: 56, minWidth: 56 }}
        />
      </Box>
      <AmountLineCalculatedSummary
        net={formatCurrencyAmount(lineAmounts.net, currency)}
        vat={formatCurrencyAmount(lineAmounts.vat, currency)}
      />
    </Box>
  );
};
